/**
 * DepartmentAssignment (EPIC A / A1) — admin assigns a staff member to one or
 * more departments.
 *
 * Modal-shaped surface opened from ManageStaffPage's "Departments" button on a
 * staff row (clone of the TeamSection pattern). Shows:
 *   - the member's current departments (the member→departments direction,
 *     A1-S8) with a Remove button each (unassign, A1-S6)
 *   - the org's active departments to add from (assign, A1-S5)
 *
 * Assign/unassign are optimistic in departmentStore (snappy chips) and
 * reconciled against the server; the dept list member_count refreshes too so
 * the count shown on DepartmentSettings stays a single source of truth.
 *
 * Permission: rendered only when `isAdmin` (caller gates the launcher);
 * defensive `return null` for non-admins.
 *
 * Day-mode contrast + button-visibility rules from CLAUDE.md applied.
 */
import React, { useEffect, useMemo, useState } from 'react';

import useDepartmentStore from '../../../stores/departmentStore';

export default function DepartmentAssignment({
  open,
  onClose,
  member,        // {member_id, first_name, last_name, name?, role}
  isAdmin,
}) {
  const departments = useDepartmentStore((s) => s.departments);
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const fetchMemberDepts = useDepartmentStore((s) => s.fetchMemberDepts);
  const assignMemberToDept = useDepartmentStore((s) => s.assignMemberToDept);
  const unassignMemberFromDept = useDepartmentStore((s) => s.unassignMemberFromDept);

  const memberId = member?.member_id;
  const memberDepts = useMemo(
    () => (memberId && deptsByMember[memberId]) || [],
    [memberId, deptsByMember],
  );

  const [busyDeptId, setBusyDeptId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!open || !memberId) return;
    setErrorMsg(null);
    if (!deptsLoaded) fetchDepartments();
    fetchMemberDepts(memberId);
  }, [open, memberId, deptsLoaded, fetchDepartments, fetchMemberDepts]);

  const assignedIds = useMemo(
    () => new Set(memberDepts.map((d) => d.dept_id)),
    [memberDepts],
  );

  // Only ACTIVE departments are assignable (A1-S5). Already-assigned ones are
  // shown disabled so the admin can see they're covered.
  const activeDepts = useMemo(
    () => departments.filter((d) => d.active !== false),
    [departments],
  );

  const onAssign = async (dept) => {
    if (!memberId) return;
    setBusyDeptId(dept.id);
    setErrorMsg(null);
    const r = await assignMemberToDept(memberId, { dept_id: dept.id, name: dept.name });
    setBusyDeptId(null);
    if (!r.success) setErrorMsg(r.error || 'Failed to assign.');
  };

  const onUnassign = async (deptId) => {
    if (!memberId) return;
    setBusyDeptId(deptId);
    setErrorMsg(null);
    const r = await unassignMemberFromDept(memberId, deptId);
    setBusyDeptId(null);
    if (!r.success) setErrorMsg(r.error || 'Failed to remove.');
  };

  if (!open || !member) return null;
  if (!isAdmin) return null;

  const memberName = member.name
    || `${member.first_name || ''} ${member.last_name || ''}`.trim()
    || memberId;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-xl overflow-hidden text-slate-900 dark:text-slate-100">
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/40">
          <div>
            <h2 className="text-base font-bold text-teal-900 dark:text-teal-100">
              Departments — {memberName}
            </h2>
            <p className="text-xs font-medium text-teal-800 dark:text-teal-200">
              Assign this staff member to one or more departments. Membership is
              data-only — it does not change what they can access.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        {errorMsg && (
          <div className="mx-5 mt-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded p-2">
            {errorMsg}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
          {/* Current departments (member → departments) */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase">
              Assigned ({memberDepts.length})
            </div>
            {memberDepts.length === 0 && (
              <div className="text-sm text-slate-600 dark:text-slate-300 italic py-3 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
                No departments yet. Click one on the right to assign.
              </div>
            )}
            {memberDepts.map((d) => (
              <div
                key={d.dept_id}
                className="flex items-center gap-2 p-2 rounded-md border-2 border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/30"
              >
                <div className="flex-1 min-w-0 text-sm font-semibold text-teal-900 dark:text-teal-100 truncate">
                  {d.name || d.dept_id}
                </div>
                <button
                  type="button"
                  onClick={() => onUnassign(d.dept_id)}
                  disabled={busyDeptId === d.dept_id}
                  className="text-xs font-medium px-2 py-1 rounded-md border border-red-400 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
                >
                  {busyDeptId === d.dept_id ? '…' : 'Remove'}
                </button>
              </div>
            ))}
          </div>

          {/* Add from active departments */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase">
              Add department
            </div>
            {activeDepts.length === 0 && (
              <div className="text-sm text-slate-600 dark:text-slate-300 italic py-3 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
                No active departments. Create one in Departments settings first.
              </div>
            )}
            {activeDepts.map((dept) => {
              const assigned = assignedIds.has(dept.id);
              return (
                <button
                  key={dept.id}
                  type="button"
                  onClick={() => onAssign(dept)}
                  disabled={assigned || busyDeptId === dept.id}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60 text-left"
                >
                  <span className="truncate">
                    {dept.name}
                    {dept.code && (
                      <span className="ml-2 text-xs text-slate-600 dark:text-slate-300">
                        {dept.code}
                      </span>
                    )}
                  </span>
                  <span className="text-xs font-semibold text-teal-700 dark:text-teal-200 ml-2">
                    {assigned ? 'Assigned' : busyDeptId === dept.id ? '…' : '+ Add'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t-2 border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/30">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
